import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { Feature } from '../../../types/feature';
import { Role } from '../../../types/role';
import { featureService } from '../../../services/featureService';
import { RoleService } from '../../../services/roleService';
import PageBreadcrumb from '../../../shared/PageBreadCrumb';
import {
  BU,
  buildFeatureRoleMatrix,
  getFeatureModuleKey,
  getFeatureTargetBUs,
} from '../utils/featureRoleMatrix';
import { getDeptMeta } from '../components/DeptBadge';
import SharedWithDots from '../components/SharedWithDots';
import { getFeatureIcon } from '../utils/featureIcons';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

const DEFAULT_ACTION_KEYS = [
  'create',
  'read',
  'update',
  'delete',
  'activate',
  'deactivate',
  'generate',
  'manage',
  'import',
];

interface RoleListing {
  roleId: string;
  roleName: string;
  isDefault?: boolean;
  description?: string;
}

interface OperationBucket {
  action: string;
  rolesByBU: Partial<Record<BU, RoleListing[]>>;
  totalRoles: number;
}

const FeaturePermissionsPage: React.FC = () => {
  const { featureKey: rawKey } = useParams<{ featureKey: string }>();
  const featureKey = rawKey ? decodeURIComponent(rawKey) : '';
  const navigate = useNavigate();

  const [features, setFeatures] = useState<Feature[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [openActions, setOpenActions] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [allFeatures, allRoles] = await Promise.all([
        featureService.getAllFeaturesForDept(undefined),
        RoleService.getAllRolesForDDL(),
      ]);
      setFeatures(allFeatures);
      setRoles(allRoles);
    } catch {
      toast.error('Failed to load feature permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const feature = useMemo(
    () => features.find(f => f.key === featureKey),
    [features, featureKey],
  );

  const matrix = useMemo(
    () => (feature ? buildFeatureRoleMatrix(features, roles) : {}),
    [features, roles, feature],
  );

  const operations = useMemo<OperationBucket[]>(() => {
    if (!feature) return [];
    const actionList =
      feature.actions && feature.actions.length > 0
        ? feature.actions
        : DEFAULT_ACTION_KEYS;
    const entry = matrix[feature.key];
    if (!entry) return actionList.map(a => ({ action: a, rolesByBU: {}, totalRoles: 0 }));

    return actionList.map(action => {
      const rolesByBU: Partial<Record<BU, RoleListing[]>> = {};
      let total = 0;
      for (const bu of entry.targetBUs) {
        const buEntry = entry.byBU[bu];
        if (!buEntry) continue;
        const withAction = buEntry.rolesWithAccess
          .filter(r => r.actions.includes(action))
          .map<RoleListing>(r => ({
            roleId: r.roleId,
            roleName: r.roleName,
            isDefault: r.isDefault,
            description: r.description,
          }));
        if (withAction.length > 0) {
          rolesByBU[bu] = withAction;
          total += withAction.length;
        }
      }
      return { action, rolesByBU, totalRoles: total };
    });
  }, [feature, matrix]);

  const toggleAction = (action: string) =>
    setOpenActions(prev => {
      const next = new Set(prev);
      if (next.has(action)) next.delete(action);
      else next.add(action);
      return next;
    });

  const backHref = `/admin/features?bu=${(getFeatureTargetBUs(feature ?? ({} as Feature))[0] ?? 'OMS').toLowerCase()}`;

  return (
    <div className="min-h-screen" style={{ background: SOVEREIGN.paper }}>
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6">
        <PageBreadcrumb
          pageTitle="Feature permissions"
          breadcrumbs={[
            { name: 'Home', path: '/' },
            { name: 'Admin', path: '/admin' },
            { name: 'Features', path: '/admin/features' },
            { name: feature?.name ?? featureKey },
          ]}
        />

        <div className="mb-4">
          <button
            type="button"
            onClick={() => navigate(backHref)}
            className="text-xs font-semibold transition-colors"
            style={{ color: SOVEREIGN.bottle }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.title; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.bottle; }}
          >
            ← Back to Features
          </button>
        </div>

        {loading ? (
          <div
            className="rounded-2xl p-8 animate-pulse space-y-4"
            style={{ background: SOVEREIGN.cardBg, border: `1px solid ${SOVEREIGN.cardBorder}` }}
          >
            <div className="h-6 rounded w-1/3" style={{ background: 'rgba(11,59,46,0.06)' }} />
            <div className="h-4 rounded w-2/3" style={{ background: 'rgba(11,59,46,0.04)' }} />
            <div className="h-32 rounded" style={{ background: 'rgba(11,59,46,0.03)' }} />
          </div>
        ) : !feature ? (
          <NotFoundCard featureKey={featureKey} />
        ) : (
          <>
            <FeatureHeaderCard feature={feature} />

            <div
              className="mt-4 rounded-2xl overflow-hidden"
              style={{
                background: SOVEREIGN.cardBg,
                border: `1px solid ${SOVEREIGN.cardBorder}`,
                boxShadow: SOVEREIGN.cardShadow,
              }}
            >
              <div
                className="px-5 py-3 flex items-center justify-between"
                style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
              >
                <div className="text-sm font-bold" style={{ color: SOVEREIGN.title }}>
                  Supported operations
                  <span className="ml-2 text-xs font-normal" style={{ color: SOVEREIGN.kpiCaption }}>
                    ({operations.length})
                  </span>
                </div>
                <div className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                  Roles are read-only here — edit grants on the Role page.
                </div>
              </div>
              <ul style={{ borderTop: 'none' }}>
                {operations.map(op => (
                  <OperationItem
                    key={op.action}
                    operation={op}
                    isOpen={openActions.has(op.action)}
                    onToggle={() => toggleAction(op.action)}
                  />
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Header card ────────────────────────────────────────────────────────────

function FeatureHeaderCard({ feature }: { feature: Feature }) {
  const targetBUs = getFeatureTargetBUs(feature);
  const inactive = feature.isActive === false;
  const accentDept = targetBUs.length === 3 ? 'COMMON' : (targetBUs[0] ?? 'COMMON');
  const accentMeta = getDeptMeta(accentDept);
  const Icon = getFeatureIcon(feature);
  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: SOVEREIGN.cardBg,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        boxShadow: SOVEREIGN.cardShadow,
      }}
    >
      <div className="flex items-start gap-4 flex-wrap">
        {/* Champagne icon tile */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          aria-hidden
          style={{
            background: accentMeta.style.background,
            border: `1px solid ${accentMeta.style.borderColor}`,
            color: accentMeta.style.color,
          }}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-extrabold truncate" style={{ color: SOVEREIGN.title }}>
              {feature.name}
            </h1>
            {inactive ? (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider"
                style={{ background: 'rgba(159,18,57,0.08)', color: '#9F1239', border: '1px solid rgba(159,18,57,0.20)' }}
              >
                Inactive
              </span>
            ) : (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider"
                style={{
                  background: 'rgba(31,90,71,0.08)',
                  color: SOVEREIGN.bottle,
                  border: `1px solid rgba(31,90,71,0.20)`,
                }}
              >
                Active
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 flex-wrap text-xs" style={{ color: SOVEREIGN.kpiCaption }}>
            <span className="font-mono">{feature.route}</span>
            <span style={{ color: SOVEREIGN.innerRule }}>·</span>
            <span className="font-mono">{feature.permissionKey}</span>
            <span style={{ color: SOVEREIGN.innerRule }}>·</span>
            <span className="inline-flex items-center gap-1">
              <SharedWithDots targetBUs={targetBUs} size="md" />
              <span className="font-semibold text-[11px]" style={{ color: SOVEREIGN.ink }}>
                {targetBUs.length === 3
                  ? 'Common'
                  : targetBUs.length === 2
                  ? `Shared with ${targetBUs.join(', ')}`
                  : `Exclusive to ${targetBUs[0] ?? '—'}`}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Operation list item ────────────────────────────────────────────────────

function OperationItem({
  operation,
  isOpen,
  onToggle,
}: {
  operation: OperationBucket;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const { action, rolesByBU, totalRoles } = operation;
  return (
    <li style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}` }}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-3 transition-colors text-left"
        style={{ background: 'transparent' }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(11,59,46,0.025)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
      >
        <span className="flex items-center gap-3">
          <span
            className="transition-transform"
            style={{ color: SOVEREIGN.kpiCaption, display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'none' }}
            aria-hidden
          >
            ▸
          </span>
          <span className="text-sm font-semibold font-mono" style={{ color: SOVEREIGN.ink }}>
            {action}
          </span>
        </span>
        <span
          className="text-xs font-semibold"
          style={{ color: totalRoles === 0 ? SOVEREIGN.kpiCaption : SOVEREIGN.bottle }}
        >
          {totalRoles === 0 ? '(no roles)' : `${totalRoles} role${totalRoles === 1 ? '' : 's'}`}
        </span>
      </button>
      {isOpen && (
        <div className="px-5 pb-4">
          {totalRoles === 0 ? (
            <div className="text-xs italic px-2" style={{ color: SOVEREIGN.kpiCaption }}>
              No roles currently have this operation.
            </div>
          ) : (
            <div className="space-y-3">
              {(Object.keys(rolesByBU) as BU[]).map(bu => {
                const list = rolesByBU[bu] ?? [];
                const meta = getDeptMeta(bu);
                return (
                  <div
                    key={bu}
                    className="rounded-lg overflow-hidden"
                    style={{ border: `1px solid ${meta.style.borderColor}` }}
                  >
                    {/* BU group header */}
                    <div
                      className="flex items-center gap-2 px-3 py-1.5"
                      style={{ background: meta.style.background }}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: meta.style.dotColor }}
                        aria-hidden
                      />
                      <span className="text-xs font-bold" style={{ color: meta.style.color }}>
                        {bu}
                      </span>
                      <span className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                        {list.length} role{list.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <ul style={{ background: SOVEREIGN.cardBg }}>
                      {list.map(r => (
                        <li
                          key={r.roleId}
                          className="px-3 py-2 flex items-center gap-2"
                          style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}` }}
                        >
                          <span className="text-sm font-semibold truncate" style={{ color: SOVEREIGN.ink }}>
                            {r.roleName}
                          </span>
                          {r.isDefault && (
                            <span
                              className="inline-flex items-center px-1 py-0 rounded text-[9px] font-semibold"
                              style={{
                                background: 'rgba(201,169,97,0.14)',
                                color: '#8A5A08',
                                border: '1px solid rgba(201,169,97,0.30)',
                              }}
                            >
                              default
                            </span>
                          )}
                          {r.description && (
                            <span
                              className="text-[11px] truncate"
                              style={{ color: SOVEREIGN.kpiCaption }}
                              title={r.description}
                            >
                              · {r.description}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// ─── Not-found card ─────────────────────────────────────────────────────────

function NotFoundCard({ featureKey }: { featureKey: string }) {
  return (
    <div
      className="rounded-2xl py-16 text-center"
      style={{
        background: SOVEREIGN.cardBg,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        boxShadow: SOVEREIGN.cardShadow,
      }}
    >
      <div className="text-5xl mb-3 opacity-30">🔍</div>
      <div className="text-sm font-semibold" style={{ color: SOVEREIGN.ink }}>
        Feature &ldquo;{featureKey}&rdquo; not found
      </div>
      <Link
        to="/admin/features"
        className="inline-block mt-4 text-xs font-semibold px-4 py-2 rounded-lg text-white transition-colors"
        style={{ background: SOVEREIGN.btn }}
      >
        Back to Features
      </Link>
    </div>
  );
}

export default FeaturePermissionsPage;
